import { useMemo, useRef } from "react";

import { usePlayground } from "@/components/Playground/playground-store";
import { PlaceholderContainerStore } from "@/components/Playground/Messages/PlaceholderContainer/stores/placeholder-container-store";
import { ChildMessagesStore } from "@/components/Playground/Messages/PlaceholderContainer/stores/child-messages-store";
import { useChildStoresMap } from "@/components/Playground/Messages/PlaceholderContainer/hooks/useChildStoresMap";

//TODO - create mixin for the stores into a single store with seprate nested store entities
export const usePlaceholderContainerStore = () => {
  const playground = usePlayground();
  const {
    storesMap: childMessageStoresMap,
    getOrCreateStore,
    initializeStores,
  } = useChildStoresMap();
  const variableSets = playground.input_variable_sets;

  const placeholderStoreRef = useRef<PlaceholderContainerStore>();
  if (!placeholderStoreRef.current) {
    placeholderStoreRef.current = new PlaceholderContainerStore(
      playground.activeInputVariableSetId ?? variableSets[0]?.id ?? "",
    );
  }
  const placeholderStore = placeholderStoreRef.current;

  // Get the active childStore (or create empty one as fallback)
  const childMessageStore: ChildMessagesStore = placeholderStore.activeSetId
    ? getOrCreateStore(placeholderStore.activeSetId)
    : new ChildMessagesStore();

  // Initialize stores for all sets synchronously during render so they exist
  // before child effects (useBiSyncPlaceholder) set up MobX reactions.
  useMemo(() => {
    initializeStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableSets.length, initializeStores]);

  // Keep activeSetId valid when variable sets change (synchronous, before children render)
  useMemo(() => {
    const validIds = new Set(variableSets.map(s => s.id));
    const current = placeholderStore.activeSetId;

    if (validIds.size === 0) {
      placeholderStore.setActiveSetId("");
      return;
    }

    if (playground.activeInputVariableSetId && playground.activeInputVariableSetId !== current) {
      placeholderStore.setActiveSetId(playground.activeInputVariableSetId);
      return;
    }

    if (!current || !validIds.has(current)) {
      placeholderStore.setActiveSetId(variableSets[0].id);
      playground.setActiveInputVariableSetId(variableSets[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableSets.length, placeholderStore, playground]);

  return { placeholderStore, childMessageStore, childMessageStoresMap };
};
